import React from 'react';
import { AlertTriangle, CheckCircle2, Pencil, ShieldAlert } from 'lucide-react';
import type { OfferRow } from '../../data/clientEngagementMock';
import { formatNlDate, offerStatusLabel } from './engagementLabels';
import InlineFeedbackNote from './InlineFeedbackNote';

function riskLabel(level: OfferRow['riskLevel']): { text: string; className: string } {
  if (level === 'high') return { text: 'Hoog risico', className: 'bg-red-100 text-red-900 ring-red-200' };
  if (level === 'medium') return { text: 'Let op', className: 'bg-amber-100 text-amber-900 ring-amber-200' };
  return { text: 'Laag risico', className: 'bg-emerald-100 text-emerald-900 ring-emerald-200' };
}

const OfferCard: React.FC<{
  offer: OfferRow;
  noteText: string;
  onAutosaveNote: (id: string, text: string) => void;
  onSendHeadsUp: (id: string, text: string) => void;
  onAkkoord: (id: string) => void;
  onWijziging: (id: string) => void;
  onRisico: (id: string) => void;
  /** Sla commentaar op in placements.data (persistent). */
  onSavePlacementComment?: (id: string, text: string) => void | Promise<void>;
}> = ({ offer, noteText, onAutosaveNote, onSendHeadsUp, onAkkoord, onWijziging, onRisico, onSavePlacementComment }) => {
  const risk = riskLabel(offer.riskLevel);
  return (
    <article className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm ring-1 ring-slate-100/80">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-bold text-slate-900">{offer.candidateName}</h3>
          <p className="text-sm text-slate-500">{offer.vacancyTitle}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-slate-700 ring-1 ring-slate-200">
            {offerStatusLabel(offer.status)}
          </span>
          <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold ring-1 ${risk.className}`}>
            <ShieldAlert className="h-3.5 w-3.5" aria-hidden />
            {risk.text}
          </span>
        </div>
      </div>
      <dl className="mt-4 grid gap-2 text-sm sm:grid-cols-2">
        <div className="rounded-xl border border-slate-100 bg-slate-50/70 p-3">
          <dt className="text-[11px] font-bold uppercase text-slate-400">Salaris</dt>
          <dd className="font-semibold text-slate-800">{offer.salaryRange}</dd>
        </div>
        <div className="rounded-xl border border-slate-100 bg-slate-50/70 p-3">
          <dt className="text-[11px] font-bold uppercase text-slate-400">Startdatum</dt>
          <dd className="font-semibold text-slate-800">{formatNlDate(offer.startDate)}</dd>
        </div>
        {offer.endDateIso ? (
          <div className="rounded-xl border border-slate-100 bg-slate-50/70 p-3 sm:col-span-2">
            <dt className="text-[11px] font-bold uppercase text-slate-400">Einddatum opdracht</dt>
            <dd className="font-semibold text-slate-800">{formatNlDate(offer.endDateIso)}</dd>
          </div>
        ) : null}
      </dl>
      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => onAkkoord(offer.id)}
          className="inline-flex items-center gap-1.5 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-900 shadow-sm hover:bg-emerald-100"
        >
          <CheckCircle2 className="h-4 w-4" aria-hidden />
          Akkoord
        </button>
        <button
          type="button"
          onClick={() => onWijziging(offer.id)}
          className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-800 shadow-sm hover:bg-slate-50"
        >
          <Pencil className="h-4 w-4" aria-hidden />
          Wijziging vragen
        </button>
        <button
          type="button"
          onClick={() => onRisico(offer.id)}
          className="inline-flex items-center gap-1.5 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs font-bold text-red-900 shadow-sm hover:bg-red-100"
        >
          <AlertTriangle className="h-4 w-4" aria-hidden />
          Markeer risico
        </button>
      </div>
      <div className="mt-4 border-t border-slate-100 pt-4">
        <p className="mb-2 text-[11px] font-bold uppercase text-slate-400">Commentaar voor recruitment</p>
        <InlineFeedbackNote
          entityKey={`off-${offer.id}`}
          initialText={noteText}
          onAutosave={(t) => onAutosaveNote(offer.id, t)}
          onSendHeadsUp={(t) => onSendHeadsUp(offer.id, t)}
        />
        {onSavePlacementComment ? (
          <button
            type="button"
            onClick={() => void onSavePlacementComment(offer.id, noteText)}
            className="mt-3 w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 text-xs font-semibold text-slate-800 transition hover:bg-slate-100"
          >
            Commentaar opslaan in dossier
          </button>
        ) : null}
      </div>
    </article>
  );
};

export default OfferCard;
